import React from 'react';
import { Navbar } from '../layouts/Navbar';
import { Footer } from '../layouts/Footer';

export const PrivacyPolicy = () => {
    // Footer header content for privacy policy page
    const footerHeader = ["Your Privacy Matters", "We protect your personal information"];

    const handleDownloadPDF = () => {
        // Create a new window for printing/PDF generation
        const printWindow = window.open('', '_blank');
        
        // Get the privacy policy content
        const content = `
            <html>
                <head>
                    <title>Privacy Policy - Clinqo</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                        h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
                        h2 { color: #374151; margin-top: 30px; }
                        h3 { color: #4b5563; margin-top: 20px; }
                        ul { margin-left: 20px; }
                        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; }
                        @media print { body { margin: 20px; } }
                    </style>
                </head>
                <body>
                    <h1>Privacy Policy for Clinqo</h1>
                    <p><strong>Last updated:</strong> September 6, 2025</p>
                    
                    <h2>1. Introduction</h2>
                    <p>Welcome to Clinqo, an innovative healthcare queue management platform developed by N6T Technologies. At N6T Technologies ("we", "our", or "us"), we are committed to protecting your privacy and ensuring the security of your personal information.</p>
                    <p>This Privacy Policy explains how we collect, use, share, and protect your information when you use our Clinqo platform and related services. We believe in transparency and want you to understand exactly how your data is handled, including our WhatsApp integration features that enhance your healthcare experience.</p>
                    <p>By using Clinqo, you acknowledge that you have read and understood this Privacy Policy. If you have any questions or concerns, please don't hesitate to contact us using the information provided at the end of this document.</p>

                    <h2>2. Information We Collect</h2>
                    <h3>2.1 Patient Information</h3>
                    <ul>
                        <li>Full name</li>
                        <li>Date of birth</li>
                        <li>Gender</li>
                        <li>Contact information (phone number, email address)</li>
                        <li>Medical symptoms and related health information</li>
                        <li>Appointment history and preferences</li>
                    </ul>

                    <h3>2.2 Technical Information</h3>
                    <ul>
                        <li>Device information</li>
                        <li>Usage data</li>
                        <li>IP address</li>
                        <li>Browser type and version</li>
                        <li>Session information</li>
                    </ul>

                    <h3>2.3 WhatsApp Integration</h3>
                    <p>When you opt into our WhatsApp services, we collect:</p>
                    <ul>
                        <li>WhatsApp phone number</li>
                        <li>Message delivery status</li>
                        <li>Interaction history with our WhatsApp services</li>
                    </ul>

                    <h2>3. How We Use Your Information</h2>
                    <h3>3.1 Core Services</h3>
                    <ul>
                        <li>Managing appointments and queue system</li>
                        <li>Providing healthcare services</li>
                        <li>Sending appointment reminders and updates</li>
                        <li>Processing payments</li>
                        <li>Maintaining medical records</li>
                    </ul>

                    <h3>3.2 WhatsApp Communication</h3>
                    <ul>
                        <li>Sending appointment confirmations</li>
                        <li>Delivering queue status updates</li>
                        <li>Providing important healthcare reminders</li>
                        <li>Sending follow-up notifications</li>
                    </ul>

                    <h3>3.3 Service Improvement</h3>
                    <ul>
                        <li>Analyzing usage patterns</li>
                        <li>Improving user experience</li>
                        <li>Enhancing service efficiency</li>
                        <li>Developing new features</li>
                    </ul>

                    <h2>4. Data Sharing and Disclosure</h2>
                    <p>We share your information with:</p>
                    <ul>
                        <li>Healthcare providers within your clinic</li>
                        <li>Payment processors for transaction handling</li>
                        <li>WhatsApp Business API for message delivery</li>
                        <li>Third-party service providers under strict confidentiality agreements</li>
                    </ul>

                    <h2>5. Data Protection</h2>
                    <p>We implement robust security measures including:</p>
                    <ul>
                        <li>Encryption of sensitive data</li>
                        <li>Secure data storage systems</li>
                        <li>Regular security audits</li>
                        <li>Access controls and authentication</li>
                        <li>Regular backup procedures</li>
                    </ul>

                    <h2>6. Your Rights</h2>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access your personal data</li>
                        <li>Correct inaccurate information</li>
                        <li>Request data deletion</li>
                        <li>Opt-out of WhatsApp communications</li>
                        <li>Export your data</li>
                        <li>Withdraw consent for data processing</li>
                    </ul>

                    <h2>7. WhatsApp-Specific Privacy Considerations</h2>
                    <h3>7.1 Message Processing</h3>
                    <ul>
                        <li>Messages are processed according to WhatsApp's Business API terms</li>
                        <li>End-to-end encryption is maintained where applicable</li>
                        <li>Message content is used only for intended service delivery</li>
                    </ul>

                    <h3>7.2 Opt-In/Opt-Out</h3>
                    <ul>
                        <li>WhatsApp communications are opt-in by default</li>
                        <li>You can opt-out at any time</li>
                        <li>Opting out won't affect other services</li>
                    </ul>

                    <h2>8. Data Retention</h2>
                    <p>We retain your data for:</p>
                    <ul>
                        <li>Active accounts: Duration of service usage</li>
                        <li>Inactive accounts: Up to 24 months</li>
                        <li>Medical records: As required by law</li>
                        <li>WhatsApp messages: Up to 12 months</li>
                    </ul>

                    <h2>9. International Data Transfers</h2>
                    <p>If we transfer your data internationally, we ensure:</p>
                    <ul>
                        <li>Adequate data protection measures</li>
                        <li>Compliance with relevant regulations</li>
                        <li>Appropriate security safeguards</li>
                    </ul>

                    <h2>10. Children's Privacy</h2>
                    <p>We do not knowingly collect data from children under 13 without parental consent.</p>

                    <h2>11. Changes to This Policy</h2>
                    <p>We may update this privacy policy periodically. We will notify you of any significant changes through:</p>
                    <ul>
                        <li>Email notification</li>
                        <li>Application notifications</li>
                        <li>Website announcements</li>
                    </ul>

                    <h2>12. Contact Information</h2>
                    <p>For privacy-related questions, you can reach us at:</p>
                    <ul>
                        <li>Company: N6T Technologies</li>
                        <li>Website: https://www.n6t.in</li>
                        <li>Email: team@n6t.in</li>
                        <li>Address: TI-109, 1st Floor, TIIR Building<br>
                            Foundation for Technology and Business Incubation (FTBI)<br>
                            National Institute of Technology<br>
                            Rourkela, Odisha - 769008<br>
                            India</li>
                    </ul>

                    <h2>13. Legal Basis for Processing</h2>
                    <p>We process your data based on:</p>
                    <ul>
                        <li>Your consent</li>
                        <li>Contractual obligations</li>
                        <li>Legal requirements</li>
                        <li>Legitimate business interests</li>
                    </ul>

                    <h2>14. Compliance</h2>
                    <p>We comply with:</p>
                    <ul>
                        <li>Local healthcare regulations</li>
                        <li>Data protection laws</li>
                        <li>WhatsApp Business Solution Terms</li>
                        <li>Industry standard security practices</li>
                    </ul>

                    <div class="footer">
                        <p>© 2025 N6T Technologies. This privacy policy is effective as of the date specified above and governs our use of your personal information.</p>
                    </div>
                </body>
            </html>
        `;
        
        printWindow.document.write(content);
        printWindow.document.close();
        
        // Wait for content to load, then trigger print dialog
        printWindow.onload = function() {
            printWindow.print();
        };
    };

    const handleContactPrivacyTeam = () => {
        // Create mailto link for privacy team
        const subject = encodeURIComponent('Privacy Policy Inquiry');
        const body = encodeURIComponent('Hello,\n\nI have a question regarding your privacy policy.\n\nBest regards');
        window.location.href = `mailto:team@n6t.in?subject=${subject}&body=${body}`;
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
                        <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto">
                            Your privacy is our priority. Learn how we protect and handle your personal information.
                        </p>
                        <p className="text-lg opacity-90 max-w-2xl mx-auto">
                            Last updated: September 6, 2025
                        </p>
                    </div>
                </div>
            </div>

            {/* Privacy Summary Section */}
            <div className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Privacy at a Glance</h2>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Quick overview of how we handle your personal information and protect your privacy.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition duration-300">
                            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Data Protection</h3>
                            <p className="text-gray-600">We use encryption and secure storage to protect your personal and medical information.</p>
                        </div>
                        <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition duration-300">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Your Rights</h3>
                            <p className="text-gray-600">You have full control over your data with rights to access, correct, or delete your information.</p>
                        </div>
                        <div className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition duration-300">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Transparency</h3>
                            <p className="text-gray-600">Clear communication about what data we collect and how we use it for healthcare services.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto bg-gray-50">
                <div className="bg-white shadow-lg rounded-lg p-8 md:p-12">
                    <div className="prose prose-lg max-w-none">
                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h2>
                        <p className="text-gray-700 mb-4">
                            Welcome to Clinqo, an innovative healthcare queue management platform developed by N6T Technologies. At N6T Technologies ("we", "our", or "us"), we are committed to protecting your privacy and ensuring the security of your personal information.
                        </p>
                        <p className="text-gray-700 mb-4">
                            This Privacy Policy explains how we collect, use, share, and protect your information when you use our Clinqo platform and related services. We believe in transparency and want you to understand exactly how your data is handled, including our WhatsApp integration features that enhance your healthcare experience.
                        </p>
                        <p className="text-gray-700 mb-4">
                            By using Clinqo, you acknowledge that you have read and understood this Privacy Policy. If you have any questions or concerns, please don't hesitate to contact us using the information provided at the end of this document.
                        </p>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
                        
                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Patient Information</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Full name</li>
                            <li>Date of birth</li>
                            <li>Gender</li>
                            <li>Contact information (phone number, email address)</li>
                            <li>Medical symptoms and related health information</li>
                            <li>Appointment history and preferences</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Technical Information</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Device information</li>
                            <li>Usage data</li>
                            <li>IP address</li>
                            <li>Browser type and version</li>
                            <li>Session information</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.3 WhatsApp Integration</h3>
                        <p className="text-gray-700 mb-2">When you opt into our WhatsApp services, we collect:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>WhatsApp phone number</li>
                            <li>Message delivery status</li>
                            <li>Interaction history with our WhatsApp services</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
                        
                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.1 Core Services</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Managing appointments and queue system</li>
                            <li>Providing healthcare services</li>
                            <li>Sending appointment reminders and updates</li>
                            <li>Processing payments</li>
                            <li>Maintaining medical records</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.2 WhatsApp Communication</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Sending appointment confirmations</li>
                            <li>Delivering queue status updates</li>
                            <li>Providing important healthcare reminders</li>
                            <li>Sending follow-up notifications</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">3.3 Service Improvement</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Analyzing usage patterns</li>
                            <li>Improving user experience</li>
                            <li>Enhancing service efficiency</li>
                            <li>Developing new features</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Data Sharing and Disclosure</h2>
                        <p className="text-gray-700 mb-2">We share your information with:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Healthcare providers within your clinic</li>
                            <li>Payment processors for transaction handling</li>
                            <li>WhatsApp Business API for message delivery</li>
                            <li>Third-party service providers under strict confidentiality agreements</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Data Protection</h2>
                        <p className="text-gray-700 mb-2">We implement robust security measures including:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Encryption of sensitive data</li>
                            <li>Secure data storage systems</li>
                            <li>Regular security audits</li>
                            <li>Access controls and authentication</li>
                            <li>Regular backup procedures</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Your Rights</h2>
                        <p className="text-gray-700 mb-2">You have the right to:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Access your personal data</li>
                            <li>Correct inaccurate information</li>
                            <li>Request data deletion</li>
                            <li>Opt-out of WhatsApp communications</li>
                            <li>Export your data</li>
                            <li>Withdraw consent for data processing</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. WhatsApp-Specific Privacy Considerations</h2>
                        
                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.1 Message Processing</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Messages are processed according to WhatsApp's Business API terms</li>
                            <li>End-to-end encryption is maintained where applicable</li>
                            <li>Message content is used only for intended service delivery</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">7.2 Opt-In/Opt-Out</h3>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>WhatsApp communications are opt-in by default</li>
                            <li>You can opt-out at any time</li>
                            <li>Opting out won't affect other services</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Data Retention</h2>
                        <p className="text-gray-700 mb-2">We retain your data for:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Active accounts: Duration of service usage</li>
                            <li>Inactive accounts: Up to 24 months</li>
                            <li>Medical records: As required by law</li>
                            <li>WhatsApp messages: Up to 12 months</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. International Data Transfers</h2>
                        <p className="text-gray-700 mb-2">If we transfer your data internationally, we ensure:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Adequate data protection measures</li>
                            <li>Compliance with relevant regulations</li>
                            <li>Appropriate security safeguards</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Children's Privacy</h2>
                        <p className="text-gray-700 mb-4">
                            We do not knowingly collect data from children under 13 without parental consent.
                        </p>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Changes to This Policy</h2>
                        <p className="text-gray-700 mb-2">We may update this privacy policy periodically. We will notify you of any significant changes through:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Email notification</li>
                            <li>Application notifications</li>
                            <li>Website announcements</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">12. Contact Information</h2>
                        <p className="text-gray-700 mb-2">For privacy-related questions, you can reach us at:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Company: N6T Technologies</li>
                            <li>Website: https://www.n6t.in</li>
                            <li>Email: team@n6t.in</li>
                            <li>Address: TI-109, 1st Floor, TIIR Building<br />
                                Foundation for Technology and Business Incubation (FTBI)<br />
                                National Institute of Technology<br />
                                Rourkela, Odisha - 769008<br />
                                India
                            </li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">13. Legal Basis for Processing</h2>
                        <p className="text-gray-700 mb-2">We process your data based on:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Your consent</li>
                            <li>Contractual obligations</li>
                            <li>Legal requirements</li>
                            <li>Legitimate business interests</li>
                        </ul>

                        <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">14. Compliance</h2>
                        <p className="text-gray-700 mb-2">We comply with:</p>
                        <ul className="list-disc pl-6 mb-4 text-gray-700">
                            <li>Local healthcare regulations</li>
                            <li>Data protection laws</li>
                            <li>WhatsApp Business Solution Terms</li>
                            <li>Industry standard security practices</li>
                        </ul>

                        <hr className="my-8 border-gray-300" />
                        
                        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-600 text-sm text-center">
                                © 2025 N6T Technologies. This privacy policy is effective as of the date specified above and governs our use of your personal information.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Section */}
            <div className="bg-blue-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold mb-4">Questions About Privacy?</h2>
                    <p className="text-xl mb-8 max-w-2xl mx-auto">
                        Our privacy team is here to help you understand how we protect your information.
                    </p>
                    <div className="space-x-4">
                        <button 
                            onClick={handleContactPrivacyTeam}
                            className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition duration-300"
                        >
                            Contact Privacy Team
                        </button>
                        <button 
                            onClick={handleDownloadPDF}
                            className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition duration-300"
                        >
                            Download Policy PDF
                        </button>
                    </div>
                    <div className="mt-8 text-lg opacity-90">
                        <p>Email: team@n6t.in </p>
                    </div>
                </div>
            </div>

            <Footer header={footerHeader} />
        </div>
    );
};
